import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, Shield, ShieldCheck, Clock, BarChart3, CheckCircle2, Zap, Calculator, DollarSign, TrendingUp, AlertCircle, Building2, Calendar, Users, FileCheck2, Mail, Lock, Database, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
type SlideType = {
  id: number;
  title: string;
  subtitle?: string;
  tagline?: string;
  note: string;
  layout: string;
};
const SLIDES: SlideType[] = [{
  id: 1,
  title: 'Quote Speed OS',
  subtitle: 'BlockQuote.ai',
  tagline: '24 hours → 4 hours. Deterministic quoting + guardrails + holds — built for Lomas.',
  note: 'Set the frame in one number.',
  layout: 'title'
}, {
  id: 2,
  title: 'Email ping-pong kills deals.',
  note: 'Anchor the pain in a number Vicente already feels.',
  layout: 'problem'
}, {
  id: 3,
  title: 'Time-to-first-quote drives conversion.',
  note: 'Make the 4-hour KPI the north star.',
  layout: 'kpi'
}, {
  id: 4,
  title: 'Deterministic quote engine + guardrails + holds.',
  note: 'Simple solution in three nouns.',
  layout: 'solution'
}, {
  id: 5,
  title: 'How it works.',
  note: 'Six steps, one rail.',
  layout: 'pipeline'
}, {
  id: 6,
  title: 'Guardrails. AI assists; pricing is deterministic.',
  note: 'Floors, blackouts, concessions, approvals.',
  layout: 'guardrails'
}, {
  id: 7,
  title: 'The dashboard your revenue leader actually opens.',
  note: 'SLA, funnel, blockers.',
  layout: 'dashboards'
}, {
  id: 8,
  title: 'The business case.',
  note: 'Leakage savings (strict) + adoption uplift (separate).',
  layout: 'business-case'
}, {
  id: 9,
  title: 'Pilot plan.',
  note: 'Week 0–3 stand-up, Week 4–9 scale.',
  layout: 'pilot'
}, {
  id: 10,
  title: 'Salesforce-safe integration.',
  note: 'System of record stays put.',
  layout: 'salesforce'
}, {
  id: 11,
  title: 'Why now. Why Lomas.',
  note: 'Destination Royale first, then scale.',
  layout: 'why-lomas'
}, {
  id: 12,
  title: 'Next steps.',
  note: 'Owners, data pack, kickoff date.',
  layout: 'next-steps'
}];
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.07,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function ResortExecutionDeck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const slide = SLIDES[currentIndex];
  const paginate = useCallback((newDirection: number) => {
    const newIndex = currentIndex + newDirection;
    if (newIndex >= 0 && newIndex < SLIDES.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
    }
  }, [currentIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') paginate(1);
      if (e.key === 'ArrowLeft') paginate(-1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const SlideHeader = ({
    title
  }: {
    title: string;
  }) => <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-2711">
      {title}
    </h2>;
  const renderSlideContent = (slide: SlideType) => {
    switch (slide.layout) {
      // ============================================
      // 1. TITLE
      // ============================================
      case 'title':
        return <div className="text-center space-y-8 flex flex-col items-center justify-center min-h-[60vh]" data-id="element-2712">
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="flex items-center gap-3 justify-center" data-id="element-2713">
              <div className="w-8 h-8 bg-red-600 flex items-center justify-center" data-id="element-2714">
                <span className="font-serif text-lg font-bold text-white" data-id="element-2715">
                  B
                </span>
              </div>
              <span className="font-mono text-sm uppercase tracking-widest text-stone-400" data-id="element-2716">
                {slide.subtitle}
              </span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="font-serif text-6xl md:text-8xl tracking-tight text-stone-50 leading-[1.05]" data-id="element-2717">
              {slide.title}
            </motion.h1>
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 0.5,
            duration: 0.8
          }} className="w-24 h-1 bg-red-600 mx-auto" data-id="element-2718" />
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="space-y-4 max-w-2xl mx-auto" data-id="element-2719">
              <div className="flex items-center justify-center gap-6 font-mono text-sm uppercase tracking-widest" data-id="element-2720">
                <span className="text-stone-500" data-id="element-2721">24 hours</span>
                <ArrowRight className="w-4 h-4 text-red-500" data-id="element-2722" />
                <span className="text-stone-50" data-id="element-2723">4 hours</span>
              </div>
              <p className="text-lg text-stone-400" data-id="element-2724">{slide.tagline}</p>
            </motion.div>
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8
          }} className="inline-flex items-center gap-2 px-3 py-1 border border-stone-800 font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2725">
              <Shield className="w-3 h-3 text-emerald-500" data-id="element-2726" />
              Salesforce-safe · Quoting rail only
            </motion.div>
          </div>;
      // ============================================
      // 2. PROBLEM
      // ============================================
      case 'problem':
        return <div className="space-y-10 w-full" data-id="element-2727">
            <SlideHeader title={slide.title} data-id="element-2728" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2729">
              Every hour spent quoting is an hour the group is shopping
              somewhere else. The cost shows up in three places.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2730">
              {[{
              icon: Clock,
              metric: '48–72h',
              label: 'Response Time',
              desc: 'Quotes that should take minutes take days. Groups book elsewhere while your team prices.'
            }, {
              icon: TrendingUp,
              metric: '−18%',
              label: 'Lost Conversion',
              desc: 'Conversion drops sharply after the 24-hour mark. Every hour past the SLA is margin walking out.'
            }, {
              icon: DollarSign,
              metric: '15–25%',
              label: 'Wholesale Leakage',
              desc: 'Groups you could have closed direct get rerouted through wholesalers who keep the margin.'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-stone-800 bg-stone-900/30 space-y-4" data-id="element-2731">
                  <item.icon className="w-7 h-7 text-red-600" data-id="element-2732" />
                  <p className="font-mono text-5xl text-stone-50" data-id="element-2733">
                    {item.metric}
                  </p>
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2734">
                    {item.label}
                  </p>
                  <p className="text-stone-400 leading-relaxed text-sm" data-id="element-2735">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>;
      // ============================================
      // 3. KPI — Why 4 Hours
      // ============================================
      case 'kpi':
        return <div className="space-y-10 w-full" data-id="element-2736">
            <SlideHeader title={slide.title} data-id="element-2737" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2738">
              Speed-to-first-quote is the single biggest predictor of close rate
              in group business. The math is unforgiving.
            </p>

            <div className="border border-stone-800 bg-stone-900/30 p-10" data-id="element-2739">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end" data-id="element-2740">
                {[{
                time: '< 4h',
                rate: '47%',
                color: 'text-emerald-400',
                tone: 'bg-emerald-500',
                h: '100%'
              }, {
                time: '4–12h',
                rate: '34%',
                color: 'text-stone-50',
                tone: 'bg-stone-400',
                h: '72%'
              }, {
                time: '12–24h',
                rate: '21%',
                color: 'text-stone-300',
                tone: 'bg-stone-600',
                h: '44%'
              }, {
                time: '> 24h',
                rate: '8%',
                color: 'text-red-400',
                tone: 'bg-red-700',
                h: '18%'
              }].map((b, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex flex-col items-center gap-3" data-id="element-2741">
                    <p className={`font-mono text-3xl ${b.color}`} data-id="element-2742">{b.rate}</p>
                    <div className="w-full h-48 flex items-end" data-id="element-2743">
                      <div className={`${b.tone} w-full`} style={{
                    height: b.h
                  }} data-id="element-2744" />
                    </div>
                    <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2745">
                      {b.time}
                    </p>
                  </motion.div>)}
              </div>
              <p className="text-center text-xs font-mono uppercase tracking-widest text-stone-500 mt-8" data-id="element-2746">
                Quote-to-Hold Conversion Rate by Response Time
              </p>
            </div>

            <div className="border-l-2 border-red-600 pl-6" data-id="element-2747">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-2" data-id="element-2748">
                The target
              </p>
              <p className="text-2xl text-stone-50 font-serif leading-snug" data-id="element-2749">
                Get to first quote in ≤ 4 hours and conversion jumps roughly{' '}
                <span className="text-red-500" data-id="element-2750">2.2×</span> versus
                same-day-but-late.
              </p>
            </div>
          </div>;
      // ============================================
      // 4. SOLUTION OVERVIEW
      // ============================================
      case 'solution':
        return <div className="space-y-10 w-full" data-id="element-2751">
            <SlideHeader title={slide.title} data-id="element-2752" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2753">
              Three operating pieces. One quoting rail. AI assists; pricing is
              deterministic from rate sheets + guardrails.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2754">
              {[{
              icon: Calculator,
              step: '01',
              title: 'Deterministic Quote Engine',
              desc: 'Rates come from your rate sheets, occupancy rules, and concession matrix. The engine produces Good/Better/Best — never invents pricing.'
            }, {
              icon: ShieldCheck,
              step: '02',
              title: 'Guardrails + Approvals',
              desc: 'Floors, blackouts, concession caps, and approval thresholds enforced automatically. In-policy ships; out-of-policy routes for sign-off.'
            }, {
              icon: Clock,
              step: '03',
              title: 'Timed Holds',
              desc: 'Inventory holds with automatic expiry, agency-facing status, and follow-up nudges. Nothing falls through the cracks.'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-stone-800 bg-stone-900/30 space-y-4" data-id="element-2755">
                  <div className="flex items-center justify-between" data-id="element-2756">
                    <item.icon className="w-7 h-7 text-red-600" data-id="element-2757" />
                    <span className="font-mono text-xs text-stone-600" data-id="element-2758">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl text-stone-50" data-id="element-2759">
                    {item.title}
                  </h3>
                  <p className="text-stone-400 leading-relaxed text-sm" data-id="element-2760">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>;
      // ============================================
      // 5. HOW IT WORKS — 6-step pipeline
      // ============================================
      case 'pipeline':
        return <div className="space-y-10 w-full" data-id="element-2761">
            <SlideHeader title={slide.title} data-id="element-2762" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2763">
              From inbound request to audited proposal — six steps, every one
              logged.
            </p>

            <div className="space-y-3" data-id="element-2764">
              {[{
              num: '01',
              title: 'Request',
              desc: 'Email, form, or WhatsApp. Any inbound channel.',
              icon: Mail
            }, {
              num: '02',
              title: 'Structured Intake',
              desc: 'AI extracts party size, dates, room mix, must-haves into a clean RFP object.',
              icon: FileCheck2
            }, {
              num: '03',
              title: 'Quotes',
              desc: 'Rules engine produces Good/Better/Best options against rate sheets.',
              icon: Calculator
            }, {
              num: '04',
              title: 'Guardrails / Approvals',
              desc: 'Out-of-policy routes to the approver. In-policy ships immediately.',
              icon: ShieldCheck
            }, {
              num: '05',
              title: 'Holds',
              desc: 'Timed inventory holds with automatic expiry and follow-up.',
              icon: Clock
            }, {
              num: '06',
              title: 'Proposal + Audit + KPIs',
              desc: 'Branded proposal link. Every step logged. KPIs updated in real time.',
              icon: BarChart3
            }].map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-center gap-6 border border-stone-800 bg-stone-900/30 p-5" data-id="element-2765">
                  <span className="font-mono text-2xl text-red-600 w-12 flex-shrink-0" data-id="element-2766">
                    {step.num}
                  </span>
                  <step.icon className="w-5 h-5 text-stone-400 flex-shrink-0" data-id="element-2767" />
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 items-baseline" data-id="element-2768">
                    <h3 className="font-serif text-xl text-stone-50" data-id="element-2769">
                      {step.title}
                    </h3>
                    <p className="text-stone-400 text-sm" data-id="element-2770">{step.desc}</p>
                  </div>
                </motion.div>)}
            </div>

            <p className="font-mono text-xs uppercase tracking-widest text-stone-500 text-center pt-2" data-id="element-2771">
              AI assists. Pricing is deterministic.
            </p>
          </div>;
      // ============================================
      // 6. GUARDRAILS
      // ============================================
      case 'guardrails':
        return <div className="space-y-10 w-full" data-id="element-2772">
            <SlideHeader title={slide.title} data-id="element-2773" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2774">
              The system says yes when policy allows yes and routes to a human
              when it doesn't. Every exception is logged.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2775">
              <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" className="border border-emerald-900/40 bg-emerald-950/10 p-6 space-y-4" data-id="element-2776">
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-500 flex items-center gap-2" data-id="element-2777">
                  <Check className="w-4 h-4" data-id="element-2778" /> Allowed
                </p>
                <ul className="space-y-3" data-id="element-2779">
                  {['Quotes within rate floor and concession limits', 'Date ranges outside blackout windows', 'Auto-approval when all guardrails pass', 'Deterministic Good/Better/Best generation', 'Multi-property comparison within policy'].map((item, i) => <li key={i} className="flex items-start gap-3 text-stone-300 text-sm" data-id="element-2780">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2781" />
                      <span data-id="element-2782">{item}</span>
                    </li>)}
                </ul>
              </motion.div>

              <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6 space-y-4" data-id="element-2783">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 flex items-center gap-2" data-id="element-2784">
                  <X className="w-4 h-4" data-id="element-2785" /> Not Allowed
                </p>
                <ul className="space-y-3" data-id="element-2786">
                  {['AI inventing or estimating rates', 'Quoting outside loaded rate sheets', 'Concessions above defined caps without approval', 'Hold extensions beyond policy', 'Silent overrides — every exception is logged'].map((item, i) => <li key={i} className="flex items-start gap-3 text-stone-400 text-sm" data-id="element-2787">
                      <X className="w-4 h-4 text-stone-600 flex-shrink-0 mt-0.5" data-id="element-2788" />
                      <span data-id="element-2789">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <div className="border-l-2 border-red-600 pl-6 max-w-3xl" data-id="element-2790">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-2" data-id="element-2791">
                The principle
              </p>
              <p className="text-lg text-stone-200 font-serif leading-snug" data-id="element-2792">
                AI assists. Pricing is deterministic from rate sheets +
                guardrails.
              </p>
            </div>
          </div>;
      // ============================================
      // 7. DASHBOARDS
      // ============================================
      case 'dashboards':
        return <div className="space-y-10 w-full" data-id="element-2793">
            <SlideHeader title={slide.title} data-id="element-2794" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2795">
              One screen. Three signals. SLA compliance, funnel health, and
              what's blocking revenue today.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2796">
              {/* SLA Compliance */}
              <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2797">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-2798">
                  SLA Compliance
                </p>
                <div className="flex items-end justify-between mb-4" data-id="element-2799">
                  <p className="font-mono text-5xl text-stone-50" data-id="element-2800">94%</p>
                  <p className="font-mono text-xs text-emerald-500" data-id="element-2801">+6 pts</p>
                </div>
                <div className="w-full h-2 bg-stone-800 mb-2" data-id="element-2802">
                  <div className="h-full bg-emerald-500" style={{
                  width: '94%'
                }} data-id="element-2803" />
                </div>
                <p className="text-xs text-stone-500" data-id="element-2804">
                  In-policy quotes responded within 4 hours
                </p>
              </motion.div>

              {/* Funnel */}
              <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2805">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-2806">
                  Funnel
                </p>
                <div className="space-y-2" data-id="element-2807">
                  {[{
                  label: 'Quote',
                  val: '100%',
                  w: '100%',
                  t: 'bg-stone-700'
                }, {
                  label: 'Hold',
                  val: '47%',
                  w: '47%',
                  t: 'bg-stone-500'
                }, {
                  label: 'Contract',
                  val: '32%',
                  w: '32%',
                  t: 'bg-red-600'
                }, {
                  label: 'Booked',
                  val: '24%',
                  w: '24%',
                  t: 'bg-emerald-500'
                }].map((row, i) => <div key={i} className="flex items-center gap-3" data-id="element-2808">
                      <span className="font-mono text-[10px] text-stone-500 w-14 uppercase tracking-widest" data-id="element-2809">
                        {row.label}
                      </span>
                      <div className="flex-1 h-5 bg-stone-900 relative" data-id="element-2810">
                        <div className={`h-full ${row.t}`} style={{
                      width: row.w
                    }} data-id="element-2811" />
                      </div>
                      <span className="font-mono text-xs text-stone-300 w-10 text-right" data-id="element-2812">
                        {row.val}
                      </span>
                    </div>)}
                </div>
              </motion.div>

              {/* Blockers */}
              <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2813">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-2814">
                  Blockers
                </p>
                <div className="space-y-3" data-id="element-2815">
                  {[{
                  text: 'Smith Wedding — missing dates',
                  tag: 'DATA',
                  color: 'bg-stone-700'
                }, {
                  text: 'TechCorp — approval pending',
                  tag: 'APPROVAL',
                  color: 'bg-amber-600'
                }, {
                  text: 'Goldman — hold expires 2h',
                  tag: 'URGENT',
                  color: 'bg-red-600'
                }].map((b, i) => <div key={i} className="flex items-center justify-between gap-2 pb-3 border-b border-stone-800 last:border-0" data-id="element-2816">
                      <p className="text-xs text-stone-300 flex-1 leading-snug" data-id="element-2817">
                        {b.text}
                      </p>
                      <span className={`font-mono text-[9px] uppercase tracking-widest text-white px-1.5 py-0.5 ${b.color}`} data-id="element-2818">
                        {b.tag}
                      </span>
                    </div>)}
                </div>
              </motion.div>
            </div>

            <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2819">
              Built for the revenue leader, not the analyst.
            </p>
          </div>;
      // ============================================
      // 8. BUSINESS CASE — Leakage + Adoption
      // ============================================
      case 'business-case':
        return <div className="space-y-8 w-full" data-id="element-2820">
            <SlideHeader title={slide.title} data-id="element-2821" />
            <p className="text-lg text-stone-400 max-w-3xl" data-id="element-2822">
              Two separate lines. Leakage savings is conservative and
              finance-reviewable. Adoption uplift is the upside.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2823">
              {/* Leakage */}
              <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6 space-y-5" data-id="element-2824">
                <div className="flex items-center justify-between" data-id="element-2825">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2826">
                    Line 1 · Leakage Savings (Verified)
                  </p>
                  <Shield className="w-4 h-4 text-emerald-500" data-id="element-2827" />
                </div>
                <h3 className="font-serif text-2xl text-stone-50 leading-snug" data-id="element-2828">
                  Reduced wholesale margin on Deal-ID-attributed volume.
                </h3>
                <div className="border-l-2 border-emerald-600 pl-4 py-1" data-id="element-2829">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-500 mb-1" data-id="element-2830">
                    Formula
                  </p>
                  <p className="font-mono text-sm text-stone-200 leading-relaxed" data-id="element-2831">
                    Savings = Σ (Direct Rate − Wholesale Rate) × Adopted Room
                    Nights
                  </p>
                </div>
                <div className="space-y-2 text-sm" data-id="element-2832">
                  {['Only Deal IDs flowing through BlockQuote count', 'Baseline agreed with finance pre-pilot', 'Measured monthly · reviewable with controller'].map((t, i) => <div key={i} className="flex items-start gap-2 text-stone-300" data-id="element-2833">
                      <Check className="w-3.5 h-3.5 text-emerald-500 mt-1 flex-shrink-0" data-id="element-2834" />
                      <span data-id="element-2835">{t}</span>
                    </div>)}
                </div>
                <div className="pt-3 border-t border-stone-800" data-id="element-2836">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-2837">
                    Illustrative — Destination Royale
                  </p>
                  <p className="font-mono text-3xl text-stone-50" data-id="element-2838">
                    $1.8M
                    <span className="text-stone-500 text-base ml-2" data-id="element-2839">
                      / year
                    </span>
                  </p>
                </div>
              </motion.div>

              {/* Adoption */}
              <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6 space-y-5" data-id="element-2840">
                <div className="flex items-center justify-between" data-id="element-2841">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2842">
                    Line 2 · Adoption Uplift (Separate)
                  </p>
                  <TrendingUp className="w-4 h-4 text-red-500" data-id="element-2843" />
                </div>
                <h3 className="font-serif text-2xl text-stone-50 leading-snug" data-id="element-2844">
                  Higher close rate from hitting the 4-hour SLA.
                </h3>
                <div className="border-l-2 border-red-600 pl-4 py-1" data-id="element-2845">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-red-500 mb-1" data-id="element-2846">
                    Driver
                  </p>
                  <p className="font-mono text-sm text-stone-200 leading-relaxed" data-id="element-2847">
                    +24 pts conversion when first quote ships in &lt; 4h
                  </p>
                </div>
                <div className="space-y-2 text-sm" data-id="element-2848">
                  {['Tracked separately from leakage line', 'Measured against same-period prior-year baseline', 'Upside, not a charge basis'].map((t, i) => <div key={i} className="flex items-start gap-2 text-stone-300" data-id="element-2849">
                      <Check className="w-3.5 h-3.5 text-red-500 mt-1 flex-shrink-0" data-id="element-2850" />
                      <span data-id="element-2851">{t}</span>
                    </div>)}
                </div>
                <div className="pt-3 border-t border-stone-800" data-id="element-2852">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-1" data-id="element-2853">
                    Illustrative — Destination Royale
                  </p>
                  <p className="font-mono text-3xl text-stone-50" data-id="element-2854">
                    +$3.2M
                    <span className="text-stone-500 text-base ml-2" data-id="element-2855">
                      / year upside
                    </span>
                  </p>
                </div>
              </motion.div>
            </div>

            <div className="border border-stone-800 bg-stone-950 p-4 flex items-start gap-3" data-id="element-2856">
              <AlertCircle className="w-4 h-4 text-stone-500 mt-0.5 flex-shrink-0" data-id="element-2857" />
              <p className="text-xs text-stone-400 leading-relaxed" data-id="element-2858">
                Strict definition: leakage savings only counts{' '}
                <span className="font-mono text-stone-300" data-id="element-2859">Deal IDs</span> that
                pass through BlockQuote. Operational changes, marketing lifts,
                and non-room revenue are explicitly excluded from the leakage
                line.
              </p>
            </div>
          </div>;
      // ============================================
      // 9. PILOT PLAN
      // ============================================
      case 'pilot':
        return <div className="space-y-10 w-full" data-id="element-2860">
            <SlideHeader title={slide.title} data-id="element-2861" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2862">
              Stand up in 21 days. Scale in the following six weeks.
            </p>

            <div data-id="element-2863">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-4" data-id="element-2864">
                Phase 1 · Week 0–3 · Stand-up
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-id="element-2865">
                {[{
                w: 'Week 0',
                t: 'Foundation',
                d: 'NDA · Owners · Drive · Kickoff'
              }, {
                w: 'Week 1',
                t: 'Configuration',
                d: 'Rates · Blackouts · Concessions · Approvals'
              }, {
                w: 'Week 2',
                t: 'Shadow Test',
                d: '10 real requests vs manual baseline'
              }, {
                w: 'Week 3',
                t: 'Go Live',
                d: '5 agencies · Live quoting · Daily KPI'
              }].map((p, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-4" data-id="element-2866">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2" data-id="element-2867">
                      {p.w}
                    </p>
                    <h4 className="font-serif text-lg text-stone-50 mb-2" data-id="element-2868">
                      {p.t}
                    </h4>
                    <p className="text-xs text-stone-400 leading-snug" data-id="element-2869">{p.d}</p>
                  </motion.div>)}
              </div>
            </div>

            <div data-id="element-2870">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-2871">
                Phase 2 · Week 4–9 · Scale
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-2872">
                {[{
                w: 'Week 4–5',
                t: 'Tune',
                d: 'Refine rules · Add agencies 6–15 · Funnel review'
              }, {
                w: 'Week 6–7',
                t: 'Expand',
                d: 'Add a second property · Approver coverage'
              }, {
                w: 'Week 8–9',
                t: 'Decision',
                d: 'Hit SLA · Verified savings reviewed · Scale call'
              }].map((p, i) => <motion.div key={i} custom={i + 4} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/20 p-4" data-id="element-2873">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2" data-id="element-2874">
                      {p.w}
                    </p>
                    <h4 className="font-serif text-lg text-stone-50 mb-2" data-id="element-2875">
                      {p.t}
                    </h4>
                    <p className="text-xs text-stone-400 leading-snug" data-id="element-2876">{p.d}</p>
                  </motion.div>)}
              </div>
            </div>

            <div className="border border-red-900/40 bg-red-950/10 p-5 flex items-center gap-4" data-id="element-2877">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-2878" />
              <p className="text-stone-200" data-id="element-2879">
                <span className="font-mono text-xs uppercase tracking-widest text-red-500 mr-3" data-id="element-2880">
                  Success
                </span>
                4-hour SLA hit for in-policy quotes by end of Week 3.
              </p>
            </div>
          </div>;
      // ============================================
      // 10. SALESFORCE-SAFE INTEGRATION
      // ============================================
      case 'salesforce':
        return <div className="space-y-10 w-full" data-id="element-2881">
            <SlideHeader title={slide.title} data-id="element-2882" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2883">
              Salesforce stays your system of record. BlockQuote is the quoting
              rail. We sync the minimum.
            </p>

            {/* Diagram */}
            <div className="border border-stone-800 bg-stone-900/30 p-8" data-id="element-2884">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-6" data-id="element-2885">
                <div className="border border-stone-700 bg-stone-950 p-5 text-center" data-id="element-2886">
                  <Users className="w-5 h-5 mx-auto mb-3 text-stone-300" data-id="element-2887" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-1" data-id="element-2888">
                    Source
                  </p>
                  <p className="font-serif text-stone-50" data-id="element-2889">
                    Agency / Planner / Direct
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-red-600 hidden md:block mx-auto" data-id="element-2890" />
                <div className="border-2 border-red-600 bg-red-950/20 p-5 text-center" data-id="element-2891">
                  <Zap className="w-5 h-5 mx-auto mb-3 text-red-500" data-id="element-2892" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-1" data-id="element-2893">
                    Quoting Rail
                  </p>
                  <p className="font-serif text-stone-50" data-id="element-2894">BlockQuote</p>
                </div>
                <ArrowRight className="w-5 h-5 text-red-600 hidden md:block mx-auto" data-id="element-2895" />
                <div className="border border-stone-700 bg-stone-950 p-5 text-center" data-id="element-2896">
                  <Database className="w-5 h-5 mx-auto mb-3 text-stone-300" data-id="element-2897" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-1" data-id="element-2898">
                    System of Record
                  </p>
                  <p className="font-serif text-stone-50" data-id="element-2899">Salesforce</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2900">
              <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2901">
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-500 mb-4 flex items-center gap-2" data-id="element-2902">
                  <Check className="w-4 h-4" data-id="element-2903" /> What we sync to Salesforce
                </p>
                <ul className="space-y-3 text-sm text-stone-300" data-id="element-2904">
                  {['Deal ID (immutable)', 'Status: Submitted → Quoted → Hold → Contracted → Closed Won/Lost', 'Timestamps for each transition', 'Deep link back to BlockQuote deal'].map((t, i) => <li key={i} className="flex items-start gap-3" data-id="element-2905">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-2906" />
                      <span data-id="element-2907">{t}</span>
                    </li>)}
                </ul>
              </div>
              <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-2908">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2" data-id="element-2909">
                  <X className="w-4 h-4" data-id="element-2910" /> What we don't do
                </p>
                <ul className="space-y-3 text-sm text-stone-400" data-id="element-2911">
                  {['Replace Salesforce or compete for pipeline ownership', 'Build pricing logic inside Salesforce', 'Require schema changes day one', 'Ingest sensitive guest PII'].map((t, i) => <li key={i} className="flex items-start gap-3" data-id="element-2912">
                      <X className="w-4 h-4 text-stone-600 flex-shrink-0 mt-0.5" data-id="element-2913" />
                      <span data-id="element-2914">{t}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-stone-800 pt-4" data-id="element-2915">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2916">
                Minimal · Reversible · Non-disruptive
              </p>
              <Link to="/salesforce" className="font-mono text-xs uppercase tracking-widest text-stone-300 hover:text-stone-50 inline-flex items-center gap-2" data-id="element-2917">
                Full integration spec <ArrowRight className="w-3 h-3" data-id="element-2918" />
              </Link>
            </div>
          </div>;
      // ============================================
      // 11. WHY NOW / WHY LOMAS
      // ============================================
      case 'why-lomas':
        return <div className="space-y-10 w-full" data-id="element-2919">
            <SlideHeader title={slide.title} data-id="element-2920" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2921">
              <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6 space-y-4" data-id="element-2922">
                <p className="font-mono text-xs uppercase tracking-widest text-red-500" data-id="element-2923">
                  Why now
                </p>
                <h3 className="font-serif text-2xl text-stone-50 leading-snug" data-id="element-2924">
                  Group demand is back. Speed is the new differentiator.
                </h3>
                <ul className="space-y-3 text-sm text-stone-300" data-id="element-2925">
                  {['Wedding & incentive volume returning above 2019 levels', 'Agencies routing through whichever resort responds first', 'Wholesale margin is the biggest line of recoverable leakage', 'Deterministic AI pricing is finally credible to revenue teams'].map((t, i) => <li key={i} className="flex items-start gap-3" data-id="element-2926">
                      <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-1" data-id="element-2927" />
                      <span data-id="element-2928">{t}</span>
                    </li>)}
                </ul>
              </motion.div>

              <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border border-red-900/40 bg-red-950/10 p-6 space-y-4" data-id="element-2929">
                <p className="font-mono text-xs uppercase tracking-widest text-red-500" data-id="element-2930">
                  Why Lomas
                </p>
                <h3 className="font-serif text-2xl text-stone-50 leading-snug" data-id="element-2931">
                  Destination Royale first. Scale to the portfolio.
                </h3>
                <ul className="space-y-3 text-sm text-stone-300" data-id="element-2932">
                  {['Single-property pilot — contained, measurable, fast', 'Existing Salesforce footprint stays untouched', 'Vicente + Dolores as named pilot owners', 'Clear path to roll across other Lomas properties at month 4'].map((t, i) => <li key={i} className="flex items-start gap-3" data-id="element-2933">
                      <Check className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-1" data-id="element-2934" />
                      <span data-id="element-2935">{t}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-id="element-2936">
              {[{
              l: 'Property',
              v: 'Destination Royale'
            }, {
              l: 'Pilot length',
              v: '9 weeks'
            }, {
              l: 'Target',
              v: '≤ 4h quote SLA'
            }, {
              l: 'Scale gate',
              v: 'Week 9 review'
            }].map((s, i) => <div key={i} className="border border-stone-800 bg-stone-900/30 p-4" data-id="element-2937">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-2" data-id="element-2938">
                    {s.l}
                  </p>
                  <p className="font-serif text-lg text-stone-50" data-id="element-2939">{s.v}</p>
                </div>)}
            </div>
          </div>;
      // ============================================
      // 12. NEXT STEPS
      // ============================================
      case 'next-steps':
        return <div className="space-y-10 w-full" data-id="element-2940">
            <SlideHeader title={slide.title} data-id="element-2941" />
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-2942">
              Three things to confirm before kickoff. Everything else is on us.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2943">
              {[{
              num: '01',
              icon: Users,
              title: 'Confirm owners',
              desc: 'Executive sponsor + revenue lead + IT contact named for the pilot.',
              detail: 'Vicente (sponsor) · Dolores (revenue) · IT contact TBC'
            }, {
              num: '02',
              icon: FileCheck2,
              title: 'Send data pack',
              desc: 'Rate sheets, blackout calendar, concession matrix, approval thresholds.',
              detail: 'Due Week 0 · Template provided'
            }, {
              num: '03',
              icon: Calendar,
              title: 'Lock kickoff date',
              desc: 'Week 0 starts on the Monday after data pack receipt.',
              detail: 'Target: kickoff within 10 days'
            }].map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6 flex flex-col" data-id="element-2944">
                  <div className="flex items-center justify-between mb-4" data-id="element-2945">
                    <step.icon className="w-6 h-6 text-red-600" data-id="element-2946" />
                    <span className="font-mono text-xs text-stone-600" data-id="element-2947">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl text-stone-50 mb-3" data-id="element-2948">
                    {step.title}
                  </h3>
                  <p className="text-sm text-stone-400 mb-4 flex-1" data-id="element-2949">
                    {step.desc}
                  </p>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-stone-500 pt-3 border-t border-stone-800" data-id="element-2950">
                    {step.detail}
                  </p>
                </motion.div>)}
            </div>

            <div className="border-t border-stone-800 pt-8 text-center space-y-3" data-id="element-2951">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500" data-id="element-2952">
                The ask
              </p>
              <p className="font-serif text-3xl text-stone-50 leading-snug max-w-3xl mx-auto" data-id="element-2953">
                Greenlight a 9-week pilot at Destination Royale. Hit the 4-hour
                SLA. Verify the savings.
              </p>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="h-screen w-full bg-stone-950 text-stone-50 overflow-hidden flex flex-col font-sans selection:bg-red-600/30" data-id="element-2954">
      {/* Top Bar */}
      <div className="flex items-center justify-between p-6 z-10 relative border-b border-stone-900/50" data-id="element-2955">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-sm uppercase tracking-widest" data-id="element-2956">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2957" />
          Back to site
        </Link>
        <div className="flex items-center gap-3" data-id="element-2958">
          <div className="w-5 h-5 bg-red-600 flex items-center justify-center" data-id="element-2959">
            <span className="font-serif text-xs font-bold text-white" data-id="element-2960">B</span>
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2961">
            Resort Execution Deck
          </span>
        </div>
        <div className="text-stone-500 font-mono text-sm" data-id="element-2962">
          {String(currentIndex + 1).padStart(2, '0')}{' '}
          <span className="text-stone-700" data-id="element-2963">/</span>{' '}
          {String(SLIDES.length).padStart(2, '0')}
        </div>
      </div>

      {/* Main Slide Area */}
      <div className="flex-1 relative flex items-start justify-center px-12 md:px-24 overflow-y-auto" data-id="element-2964">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-2965">
          <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="w-full max-w-6xl mx-auto pt-8 pb-12" data-id="element-2966">
            {renderSlideContent(slide)}
          </motion.div>
        </AnimatePresence>

        <button onClick={() => paginate(-1)} disabled={currentIndex === 0} className="absolute left-6 top-1/2 -translate-y-1/2 p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-2967">
          <ChevronLeft className="w-8 h-8" data-id="element-2968" />
        </button>
        <button onClick={() => paginate(1)} disabled={currentIndex === SLIDES.length - 1} className="absolute right-6 top-1/2 -translate-y-1/2 p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-2969">
          <ChevronRight className="w-8 h-8" data-id="element-2970" />
        </button>
      </div>

      {/* Progress */}
      <div className="flex justify-center gap-3 p-6 z-10 relative border-t border-stone-900/50" data-id="element-2971">
        {SLIDES.map((_, i) => <button key={i} onClick={() => {
        setDirection(i > currentIndex ? 1 : -1);
        setCurrentIndex(i);
      }} className={`h-1 transition-all duration-300 ${i === currentIndex ? 'bg-red-600 w-8' : 'bg-stone-800 hover:bg-stone-600 w-4'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-2972" />)}
      </div>
    </div>;
}